import { useFloating, offset, flip, shift, autoUpdate, Placement } from '@floating-ui/react'
import { useState, useEffect } from 'react'

interface CustomTooltipProps {
  children: React.ReactNode
  title: string
  description: string
  shouldShow?: boolean
}

export default function CustomTooltip({ children, title, description, shouldShow = true }: CustomTooltipProps) {
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState<Placement>('top')

  const {
    refs,
    floatingStyles,
    placement: computedPlacement,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(10), flip({ padding: 320 }), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom',
  })

  // Track placement changes
  useEffect(() => {
    setPlacement(computedPlacement)
  }, [computedPlacement])

  const getArrowStyles = () => {
    const baseStyles = 'absolute w-3 h-3 bg-[#4C4C4C] rotate-45'

    switch (placement) {
      case 'top':
        return `${baseStyles} bottom-[-6px] left-1/2 -translate-x-1/2`
      case 'bottom':
        return `${baseStyles} top-[-6px] left-1/2 -translate-x-1/2`
      case 'left':
        return `${baseStyles} right-[-6px] top-1/2 -translate-y-1/2`
      case 'right':
        return `${baseStyles} left-[-6px] top-1/2 -translate-y-1/2`
      case 'top-start':
        return `${baseStyles} bottom-[-6px] left-4`
      case 'top-end':
        return `${baseStyles} bottom-[-6px] right-4`
      case 'bottom-start':
        return `${baseStyles} top-[-6px] left-4`
      case 'bottom-end':
        return `${baseStyles} top-[-6px] right-4`
      case 'left-start':
        return `${baseStyles} right-[-6px] top-4`
      case 'left-end':
        return `${baseStyles} right-[-6px] bottom-4`
      case 'right-start':
        return `${baseStyles} left-[-6px] top-4`
      case 'right-end':
        return `${baseStyles} left-[-6px] bottom-4`
      default:
        return `${baseStyles} bottom-[-6px] left-1/2 -translate-x-1/2`
    }
  }

  return (
    <>
      <div
        ref={refs.setReference}
        onMouseEnter={() => shouldShow && setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="w-max"
      >
        {children}
      </div>

      {open && shouldShow && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="w-[333px] bg-[#4C4C4C] rounded-[6px] flex flex-col px-[12px] py-[8px] gap-[2px] shadow-[0_1px_4px_0_rgba(57,57,57,0.05),0_4px_12px_0_rgba(57,57,57,0.12)] z-[9999]"
        >
          <div className={getArrowStyles()} />
          <h3 className="font-semibold text-sm text-[#F4F4F4] mb-2">{title}</h3>
          <p className="text-xs text-white">{description}</p>
        </div>
      )}
    </>
  )
}
